import { TypeKeys } from "./TypeKeys";
import { AbstractPlainTextItem } from "./AbstractPlainTextItem";
import { JoinTextRelationalItem } from "./JoinTextRelationalItem";
import { IFontSizeAutoAdjustable } from "./IFontSizeAutoAdjustable";
import { AutoNotePageKinds } from "../AutoNotePageKinds";
import { ObjectId } from "../ObjectId";
import { IItem } from "./IItem";
import { instanceOfIJoinTextAssosiatable } from "./IJoinTextAssosiatable";
import { AbstractOptionSelectableTextItem } from "./AbstractOptionSelectableTextItem";
import { INumberingAssosiatable } from "./INumberingAssosiatable";
import { INaturalTextSpecifiable } from "./INaturalTextSpecifiable";

export class JoinTextItem extends AbstractPlainTextItem implements IFontSizeAutoAdjustable, INumberingAssosiatable, INaturalTextSpecifiable {
  constructor(value: any) {
    super(TypeKeys.JoinTextItem, value);
  }
  public joinTextRelationalItems: JoinTextRelationalItem[] = [];
  public enabledAutoAdjustFontSize: boolean = false;
  public shouldInputFreeAll: boolean = false;
  public shouldAppendNewLine: boolean = false;
  public isNaturalViewEnable: boolean;
  public naturalText: string;

  // IFontSizeAutoAdjustableで必要なので定義しているが、結合オブジェクトから続紙を追加はしないのでtranslatorで値の格納はしない
  autoNotePageSize: number = null;
  autoNotePageKind: AutoNotePageKinds = null;

  protected createCloneInstance(): JoinTextItem {
    return new JoinTextItem(this.originalValue);
  }

  protected applyMembers(clone: JoinTextItem): void {
    super.applyMembers(clone);
    clone.joinTextRelationalItems = this.cloneValue(this.joinTextRelationalItems);
    clone.enabledAutoAdjustFontSize = this.cloneValue(this.enabledAutoAdjustFontSize);
    clone.shouldInputFreeAll = this.cloneValue(this.shouldInputFreeAll);
    clone.autoNotePageSize = this.cloneValue(this.autoNotePageSize);
    clone.autoNotePageKind = this.cloneValue(this.autoNotePageKind);
    clone.shouldAppendNewLine = this.cloneValue(this.shouldAppendNewLine);
    clone.isNaturalViewEnable = this.cloneValue(this.isNaturalViewEnable);
    clone.naturalText = this.cloneValue(this.naturalText);
  }

  get isMultiLine(): boolean {
    return this.textStyle.isMultiLine;
  }

  setText(text: string): void {
    this.value = text;
  }

  get isEmpty(): boolean {
    return !this.text();
  }

  adjustFontSize(): void {
    if (!this.enabledAutoAdjustFontSize) { return; }
    const adjustedFontSize = this.getAdjustedFontSize(this.isMultiLine);
    this.textStyle.adjustedFontSize = adjustedFontSize;
  }

  getDisplayName(): string {
    return this.name;
  }

  initialize(
    itemAccessor: (finder?: ObjectId | string | ((i: IItem) => boolean)) => IItem,
    itemsAccessor: (finder?: ObjectId | string | ((i: IItem) => boolean)) => IItem[]
  ): IItem {
    return this.getReevaluatedClone(
      s => super.initialize(itemAccessor, itemsAccessor) as JoinTextItem,
      s => s.createReevaluatedCloneOnInitialize(itemAccessor, itemsAccessor),
    );
  }

  handleItemUpdated(
    originalItem: IItem,
    updatedItem: IItem,
    isInSameSection: boolean,
    itemAccessor: (finder?: ObjectId | string | ((i: IItem) => boolean), isSameSectionOnly?: boolean) => IItem,
    itemsAccessor: (finder?: ObjectId | string | ((i: IItem) => boolean), isSameSectionOnly?: boolean) => IItem[]
  ): IItem {
    return this.getReevaluatedClone(
      s => super.handleItemUpdated(originalItem, updatedItem, isInSameSection, itemAccessor, itemsAccessor) as JoinTextItem,
      s => s.createReevaluatedCloneOnItemUpdated(updatedItem, itemAccessor, itemsAccessor),
    );
  }

  handleOwnUpdated(
    originalItem: IItem,
    itemAccessor: (finder?: ObjectId | string | ((i: IItem) => boolean), isSameSectionOnly?: boolean) => IItem,
    itemsAccessor: (finder?: ObjectId | string | ((i: IItem) => boolean), isSameSectionOnly?: boolean) => IItem[]
  ): IItem {
    return this.getReevaluatedClone(
      s => super.handleOwnUpdated(originalItem, itemAccessor, itemsAccessor) as JoinTextItem,
      s => s.createReevaluatedCloneOnOwnUpdated(originalItem, itemAccessor, itemsAccessor),
    );
  }

  private createReevaluatedCloneOnInitialize(itemAccessor: (finder?: ObjectId | string | ((i: IItem) => boolean)) => IItem, itemsAccessor: (finder?: ObjectId | string | ((i: IItem) => boolean), isSameSectionOnly?: boolean) => IItem[]): JoinTextItem {
    return !this.shouldInputFree
      ? this.createReevaluatedClone(itemAccessor, itemsAccessor)
      : null;
  }

  private createReevaluatedCloneOnItemUpdated(updatedItem: IItem, itemAccessor: (finder?: string | ObjectId | ((i: IItem) => boolean), isSameSectionOnly?: boolean) => IItem, itemsAccessor: (finder?: string | ObjectId | ((i: IItem) => boolean), isSameSectionOnly?: boolean) => IItem[]): JoinTextItem {
    return (!this.shouldInputFree && !this.shouldInputFreeAll && this.isReferedItem(updatedItem))
      ? this.createReevaluatedClone(itemAccessor, itemsAccessor)
      : null;
  }

  private createReevaluatedCloneOnOwnUpdated(originalItem: IItem, itemAccessor: (finder?: ObjectId | string | ((i: IItem) => boolean)) => IItem, itemsAccessor: (finder?: ObjectId | string | ((i: IItem) => boolean), isSameSectionOnly?: boolean) => IItem[]): JoinTextItem {
    return (!this.shouldInputFree && !this.shouldInputFreeAll && this.isFreeInputChanged(originalItem))
      ? this.createReevaluatedClone(itemAccessor, itemsAccessor)
      : null;
  }

  private isFreeInputChanged(originalItem: IItem): boolean {
    return (originalItem instanceof JoinTextItem)
        && (originalItem.shouldInputFree !== this.shouldInputFree || originalItem.shouldInputFreeAll !== this.shouldInputFreeAll);
  }

  private getReevaluatedClone(...updates: ((s: JoinTextItem) => JoinTextItem)[]): JoinTextItem {
    const updated = this.getUpdatedClone<JoinTextItem>(...updates);
    if (updated) {
      updated.adjustFontSize();
    }
    return updated;
  }

  private createReevaluatedClone(itemAccessor: (finder?: string | ObjectId | ((i: IItem) => boolean), isSameSectionOnly?: boolean) => IItem, itemsAccessor: (finder?: string | ObjectId | ((i: IItem) => boolean), isSameSectionOnly?: boolean) => IItem[]): JoinTextItem {
    const joinedText = this.extractJoinedText(itemAccessor);
    if (this.value === joinedText) { return null; }
    return this.createUpdatedClone<JoinTextItem>(s => s.value = joinedText);
  }

  private extractJoinedText(itemAccessor: (finder?: string | ObjectId | ((i: IItem) => boolean), isSameSectionOnly?: boolean) => IItem): string {
    const texts = this.joinTextRelationalItems.map(j => this.getSpecifiedText(itemAccessor, j));
    const separator = this.shouldAppendNewLine ? "\r\n" : "";
    return texts.join(separator);
  }

  private getSpecifiedText(itemAccessor: (finder?: string | ObjectId | ((i: IItem) => boolean), isSameSectionOnly?: boolean) => IItem, info: JoinTextRelationalItem): string {
    if (info.itemType === "1") {
      // 可変値
      const found = itemAccessor(i => this.isJoinTarget(i, info));
      if (!instanceOfIJoinTextAssosiatable(found)) { return null; }
      return found.text();
    }
    if (info.itemType === "2") {
      // 固定値
      return info.value;
    }
    return null;
  }

  private isReferedItem(item: IItem): boolean {
    if (!this.joinTextRelationalItems) { return false; }
    for (const info of this.joinTextRelationalItems) {
      if (this.isJoinTarget(item, info)) { return true; }
    }
    return false;
  }

  private isJoinTarget(item: IItem, info: JoinTextRelationalItem): boolean {
    if (info.itemType !== "1") { return false; }
    if (!instanceOfIJoinTextAssosiatable(item)) { return false; }
    if (info.objectId) {
      if (info.objectId === item.id) {
        return true;
      }
    }
    if (info.key) {
      // リスト選択の場合は何故かObjectIdが指定されていない。
      // 名称しかキーが無くて意図しないオブジェクトとマッチしてしまう懸念があるので、対象を制限している
      if (item instanceof AbstractOptionSelectableTextItem && info.key === item.name) {
        return true;
      }
    }
    return false;
  }

  getNumberingKeyword(): string {
    return this.text();
  }

  get currentNaturalText(): string {
    return this.isNaturalViewEnable && this.naturalText
      ? this.naturalText
      : this.text();
  }
}
